<template>
    <div>
        <div
            :class="disabled ? 'opacity-50' : ''"
            class="flex items-center justify-between gap-1 rounded-lg border-1 border-gray-200 px-6 py-1 transition"
        >
            <button
                :aria-expanded="isExpanded"
                :class="{ 'cursor-pointer': !disabled, 'cursor-not-allowed': disabled }"
                :disabled="disabled"
                class="flex items-center gap-2 border-0 bg-transparent p-0 text-left text-inherit"
                type="button"
                @click.stop="toggleExpanded"
            >
                <span>{{ label }}</span>
                <ChevronUp :class="{ 'rotate-180': !isExpanded }" :stroke-width="2" class="h-6 w-6 transition-transform" />
            </button>
            <div class="flex items-center gap-1">
                <button
                    :aria-label="confirmedLabel"
                    :aria-pressed="result === true"
                    :class="{ 'cursor-pointer': !disabled, 'cursor-not-allowed': disabled }"
                    :disabled="disabled"
                    class="border-0 bg-transparent p-0"
                    type="button"
                    @click.stop="confirmFromHeader"
                >
                    <check-circle-icon :active="result === true" class="h-10 w-10" />
                </button>
                <button
                    :aria-label="declinedLabel"
                    :aria-pressed="result === false"
                    :class="{ 'cursor-pointer': !disabled, 'cursor-not-allowed': disabled }"
                    :disabled="disabled"
                    class="border-0 bg-transparent p-0"
                    type="button"
                    @click.stop="rejectFromHeader"
                >
                    <x-circle-icon :active="result === false" class="h-10 w-10" />
                </button>
            </div>
        </div>

        <div v-if="isExpanded" class="mt-2 mb-4">
            <div :class="sideBarClass" class="border-l-2 pl-4">
                <p class="text-sm text-gray-700">{{ question }}</p>

                <div class="mt-2 flex flex-col gap-2">
                    <button
                        :class="[
                            'flex items-center gap-3 !rounded-lg border px-4 py-3 text-left transition',
                            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                            result === true
                                ? 'border-green bg-green/10 text-green'
                                : 'border-panel-border bg-white text-gray-700 hover:border-green/30',
                        ]"
                        :disabled="disabled"
                        type="button"
                        @click.stop="selectConfirmed"
                    >
                        <check-circle-icon :active="result === true" class="h-6 w-6 shrink-0" />
                        <span>{{ confirmedLabel }}</span>
                    </button>
                </div>

                <div v-if="result === false" class="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {{ warning }}
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { ChevronUp } from '@lucide/vue';
import { trans } from '@/mixins/i18n';
import CheckCircleIcon from '@/Components/Icons/CheckCircleIcon.vue';
import XCircleIcon from '@/Components/Icons/XCircleIcon.vue';

const props = defineProps({
    stepId: { type: String, required: true },
    label: String,
    disabled: Boolean,
    result: {
        default: null,
        validator: (value) => value === null || typeof value === 'boolean',
    },
});

const emit = defineEmits(['change']);

// Start expanded when the step is already marked negative so the warning is visible on load.
const isExpanded = ref(props.result === false);

const question = computed(() => trans(`tasks.steps.${props.stepId}.question`));
const confirmedLabel = computed(() => trans(`tasks.steps.${props.stepId}.options.confirmed`));
const declinedLabel = computed(() => trans(`tasks.steps.${props.stepId}.options.declined`));
const warning = computed(() => trans(`tasks.steps.${props.stepId}.warning`));

const sideBarClass = computed(() => {
    if (props.result === true) return 'border-green/30';
    if (props.result === false) return 'border-red/30';
    return 'border-gray-200';
});

const toggleExpanded = () => {
    if (props.disabled) return;
    isExpanded.value = !isExpanded.value;
};

const confirmFromHeader = () => {
    if (props.disabled) return;
    emit('change', true);
};

const rejectFromHeader = () => {
    if (props.disabled) return;
    // Mark the check negative and reveal the warning with the required follow-up procedure.
    emit('change', false);
    isExpanded.value = true;
};

const selectConfirmed = () => {
    if (props.disabled) return;
    emit('change', true);
};
</script>
